/**
 * useEventContext — convenience hook to consume EventContext.
 *
 * Extracted into its own file so that react-refresh/only-export-components
 * is satisfied: EventContext.tsx exports only the EventProvider component,
 * while this file exports only the hook.
 *
 * Usage:
 *   import { useEventContext } from '../../app/provider/useEventContext';
 *   const { eventData, customerGroups, loading, error, refetchEventData } = useEventContext();
 */
import { useContext } from 'react';

import type { EventConfiguration, CustomerGroup } from '../../features/home/types/home.types';

import EventContext from './EventContext';

interface EventContextType {
  eventData: EventConfiguration | null;
  customerGroups: CustomerGroup[];
  loading: boolean;
  error: Error | null;
  refetchEventData: (forceRefresh?: boolean) => void;
}

/**
 * Convenience hook to consume EventContext.
 * Throws a clear error if called outside <EventProvider>.
 */
export function useEventContext(): EventContextType {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error('useEventContext must be used within an <EventProvider>');
  }
  return context;
}
